import { mkCreature } from '@/domain/creature/factory';
import type { Creature, Stage } from '@/domain/creature/types';
import type { Expression } from '@/runtime/render/animations';
import type { RendererPoseOverride } from '@/runtime/render/CreatureRenderer';
import type {
  AlchemyType,
  BodyGene,
  EarGene,
  EggPatternGene,
  Genome,
  HornGene,
  MarkingGene,
  TailGene,
} from '@/domain/genetics/types';

const bodies: BodyGene[] = ['round', 'tall', 'blob', 'fish', 'ghost'];
const patterns: EggPatternGene[] = [
  'plain',
  'spots',
  'patches',
  'stripes',
  'dots',
  'checks',
  'stars',
  'dark',
];
const types: AlchemyType[] = ['Fire', 'Water', 'Earth', 'Air', 'Life', 'Shadow'];
const ears: EarGene[] = ['round', 'pointy', 'floppy', 'antenna'];
const horns: HornGene[] = ['none', 'nub', 'twin', 'crown'];
const tails: TailGene[] = ['none', 'stub', 'curl', 'fin'];
const markings: MarkingGene[] = ['none', 'cheeks', 'freckles', 'stripes', 'mask'];

interface GalleryRow {
  stage: Stage;
  label: string;
  state: 'happy' | 'angry' | 'sick' | 'sad' | 'blink' | 'breathe';
  previewExpression?: Expression;
  previewPose?: RendererPoseOverride;
  animate?: boolean;
  reducedMotion?: boolean;
}

export interface CreatureGalleryEntry {
  creature: Creature;
  rowLabel: string;
  bodyLabel: BodyGene;
  previewExpression?: Expression;
  previewPose?: RendererPoseOverride;
  animate: boolean;
  reducedMotion?: boolean;
}

const rows: GalleryRow[] = [
  { stage: 'egg', label: 'egg', state: 'happy' },
  { stage: 'baby', label: 'baby happy', state: 'happy' },
  { stage: 'teen', label: 'teen angry', state: 'angry' },
  { stage: 'adult', label: 'adult sick', state: 'sick' },
  { stage: 'adult', label: 'adult blink', state: 'blink', previewExpression: 'blink' },
  { stage: 'adult', label: 'adult breathe in', state: 'breathe', previewPose: 'breathe-in', animate: false },
  { stage: 'adult', label: 'adult breathe out', state: 'breathe', previewPose: 'breathe-out', animate: false },
  { stage: 'elder', label: 'elder sad', state: 'sad' },
];

function genomeAt(row: number, col: number): Genome {
  const index = row * bodies.length + col;
  return {
    body: bodies[col]!,
    eyes: 'basic',
    mouth: 'basic',
    ears: ears[(row + col) % ears.length]!,
    horns: horns[(row * 2 + col) % horns.length]!,
    tail: tails[(row + col * 2) % tails.length]!,
    markings: markings[index % markings.length]!,
    paletteHue: (index * 37) % 360,
    bodyPredisposition: 50,
    eggPattern: patterns[index % patterns.length]!,
    primaryTypes: [types[index % types.length]!],
    modifiers: [],
    inheritedTraits: [],
  };
}

function applyGalleryState(creature: Creature, row: GalleryRow, nowMs: number): Creature {
  if (row.state === 'angry') {
    return {
      ...creature,
      state: {
        ...creature.state,
        tantrum: true,
        tantrumStartedAt: nowMs,
      },
    };
  }
  if (row.state === 'sick') {
    return {
      ...creature,
      state: {
        ...creature.state,
        sick: true,
        sickSince: nowMs,
      },
    };
  }
  if (row.state === 'sad') {
    return {
      ...creature,
      state: {
        ...creature.state,
        hunger: 12,
        happiness: 18,
        attentionLowSince: nowMs,
      },
    };
  }
  return creature;
}

export function createCreatureGalleryEntries(nowMs = Date.now()): CreatureGalleryEntry[] {
  return rows.flatMap((row, rowIndex) =>
    bodies.map((body, colIndex) => {
      const index = rowIndex * bodies.length + colIndex;
      const creature = mkCreature({
        id: `gallery-${index}`,
        name: `Himo ${String(index + 1).padStart(2, '0')}`,
        genome: genomeAt(rowIndex, colIndex),
        nowMs,
      });
      const staged = {
        ...creature,
        lifecycle: {
          ...creature.lifecycle,
          stage: row.stage,
        },
      };
      return {
        creature: applyGalleryState(staged, row, nowMs),
        rowLabel: row.label,
        bodyLabel: body,
        animate: row.animate ?? true,
        ...(row.previewExpression ? { previewExpression: row.previewExpression } : {}),
        ...(row.previewPose ? { previewPose: row.previewPose } : {}),
        ...(row.reducedMotion !== undefined ? { reducedMotion: row.reducedMotion } : {}),
      };
    }),
  );
}

export function createCreatureGallerySamples(nowMs = Date.now()): Creature[] {
  return createCreatureGalleryEntries(nowMs).map((entry) => entry.creature);
}
